import is from '@sindresorhus/is';

import { findUrlTileDefinition } from './serialize-url-tiles';
import type {
    ParsedUrlTile,
    UrlTileCommentsGroupChild,
    UrlTileCommentsGroupParent,
    UrlTileDefinition,
    UrlTilePayload,
} from './types';

export const COMMENT_TILE_SLOT_KEY_PREFIX = 'url-tiles.comment-tile';

// Focus-region label for the synthesized tile, alongside the parsed tiles that label their
// region with the definition type.
export const COMMENT_TILE_FOCUS_LABEL = 'comments-group';

export const commentTileSlotKey = (groupKey: string): string => `${COMMENT_TILE_SLOT_KEY_PREFIX}.${groupKey}`;

/**
 * Opens or closes a comments group from its owner's payload. Closing drops the field instead of
 * writing an explicit `false`, so a closed rail leaves no token behind in the URL.
 */
export const withCommentsOpen = <TPayload extends UrlTilePayload>(
    payload: TPayload,
    isCommentsOpen: boolean,
): TPayload => {
    if (isCommentsOpen) {
        return { ...payload, isCommentsOpen: true };
    }

    const { isCommentsOpen: _isCommentsOpen, ...payloadWithoutComments } = payload;

    // Dropping an optional key widens the value to Omit<TPayload, 'isCommentsOpen'>; every owner
    // payload declares the field optional, so what is left is still a TPayload.
    return payloadWithoutComments as TPayload;
};

export const isCommentTileSlotKey = (slotKey: string): boolean =>
    slotKey.startsWith(`${COMMENT_TILE_SLOT_KEY_PREFIX}.`);

export type UrlCommentTile = {
    readonly childPayloads: readonly UrlTilePayload[];
    readonly groupKey: string;
    // The open tile whose payload carries the comments-open flag: the group parent when it is
    // open, otherwise the connected child that hosts the comment tile on its own.
    readonly ownerPayload: UrlTilePayload;
    readonly ownerSlotKey: string;
    readonly slotKey: string;
};

export type OrderedUrlTile =
    | {
          readonly commentTile: UrlCommentTile;
          readonly kind: 'comment-tile';
      }
    | {
          readonly kind: 'url-tile';
          readonly tile: ParsedUrlTile;
      };

type CommentsGroup = {
    readonly childSlotKeys: readonly string[];
    readonly commentTile: UrlCommentTile;
    readonly isCommentsOpen: boolean;
    readonly lastMemberIndex: number;
    readonly parentSlotKey: string | undefined;
    readonly parentType: string;
};

const commentsGroupParentOf = (definition: UrlTileDefinition): UrlTileCommentsGroupParent | undefined => {
    const commentsGroup = definition.commentsGroup;

    return commentsGroup?.role === 'parent' ? commentsGroup : undefined;
};

const commentsGroupChildOf = (definition: UrlTileDefinition): UrlTileCommentsGroupChild | undefined => {
    const commentsGroup = definition.commentsGroup;

    return commentsGroup?.role === 'child' ? commentsGroup : undefined;
};

const isChildOfGroup = (tile: ParsedUrlTile, parentType: string, groupKey: string): boolean => {
    const child = commentsGroupChildOf(tile.definition);

    return !is.undefined(child) && child.parentType === parentType && child.groupKey(tile.payload) === groupKey;
};

const collectGroupChildren = (
    tiles: readonly ParsedUrlTile[],
    parentType: string,
    groupKey: string,
): { readonly children: readonly ParsedUrlTile[]; readonly lastChildIndex: number } => {
    const children: ParsedUrlTile[] = [];
    let lastChildIndex = -1;

    for (const [childIndex, tile] of tiles.entries()) {
        if (!isChildOfGroup(tile, parentType, groupKey)) {
            continue;
        }

        children.push(tile);
        lastChildIndex = childIndex;
    }

    return { children, lastChildIndex };
};

const isChildCommentsOpen = (tile: ParsedUrlTile): boolean =>
    commentsGroupChildOf(tile.definition)?.isCommentsOpen?.(tile.payload) === true;

const canChildHostCommentTile = (tile: ParsedUrlTile): boolean =>
    !is.undefined(commentsGroupChildOf(tile.definition)?.isCommentsOpen);

const groupIdentity = (parentType: string, groupKey: string): string => `${parentType}|${groupKey}`;

const findCommentsGroups = (tiles: readonly ParsedUrlTile[]): readonly CommentsGroup[] => {
    const groups: CommentsGroup[] = [];
    const seenGroups = new Set<string>();

    for (const [parentIndex, parentTile] of tiles.entries()) {
        const parent = commentsGroupParentOf(parentTile.definition);

        if (is.undefined(parent)) {
            continue;
        }

        const groupKey = parent.groupKey(parentTile.payload);

        if (is.undefined(groupKey)) {
            continue;
        }

        const parentType = parentTile.definition.type;
        const { children, lastChildIndex } = collectGroupChildren(tiles, parentType, groupKey);

        seenGroups.add(groupIdentity(parentType, groupKey));
        groups.push({
            childSlotKeys: children.map((child) => child.slotKey),
            commentTile: {
                childPayloads: children.map((child) => child.payload),
                groupKey,
                ownerPayload: parentTile.payload,
                ownerSlotKey: parentTile.slotKey,
                slotKey: commentTileSlotKey(groupKey),
            },
            isCommentsOpen: parent.isCommentsOpen(parentTile.payload),
            lastMemberIndex: Math.max(parentIndex, lastChildIndex),
            parentSlotKey: parentTile.slotKey,
            parentType,
        });
    }

    // Children whose parent tile is not open form a group of their own, hosted by the first
    // child that carries a comments-open flag, so a deep-linked task can still show its rail.
    for (const tile of tiles) {
        const child = commentsGroupChildOf(tile.definition);
        const groupKey = child?.groupKey(tile.payload);

        if (
            is.undefined(child) ||
            is.undefined(groupKey) ||
            seenGroups.has(groupIdentity(child.parentType, groupKey))
        ) {
            continue;
        }

        seenGroups.add(groupIdentity(child.parentType, groupKey));

        const { children, lastChildIndex } = collectGroupChildren(tiles, child.parentType, groupKey);
        const owner = children.find(isChildCommentsOpen) ?? children.find(canChildHostCommentTile);

        if (is.undefined(owner)) {
            continue;
        }

        groups.push({
            childSlotKeys: children.map((member) => member.slotKey),
            commentTile: {
                childPayloads: children.map((member) => member.payload),
                groupKey,
                ownerPayload: owner.payload,
                ownerSlotKey: owner.slotKey,
                slotKey: commentTileSlotKey(groupKey),
            },
            isCommentsOpen: isChildCommentsOpen(owner),
            lastMemberIndex: lastChildIndex,
            parentSlotKey: undefined,
            parentType: child.parentType,
        });
    }

    return groups;
};

export const orderUrlTilesWithCommentTile = (tiles: readonly ParsedUrlTile[]): readonly OrderedUrlTile[] => {
    const openGroups = findCommentsGroups(tiles).filter((group) => group.isCommentsOpen);
    const ordered: OrderedUrlTile[] = [];

    for (const [index, tile] of tiles.entries()) {
        ordered.push({ kind: 'url-tile', tile });

        for (const group of openGroups) {
            if (group.lastMemberIndex === index) {
                ordered.push({ commentTile: group.commentTile, kind: 'comment-tile' });
            }
        }
    }

    return ordered;
};

export const selectCommentTiles = (orderedTiles: readonly OrderedUrlTile[]): readonly UrlCommentTile[] =>
    orderedTiles.flatMap((entry) => (entry.kind === 'comment-tile' ? [entry.commentTile] : []));

export const commentsGroupInsertIndex = (
    tiles: readonly ParsedUrlTile[],
    payload: UrlTilePayload,
    definitions: readonly UrlTileDefinition[],
): number | undefined => {
    const definition = findUrlTileDefinition(payload, definitions);

    if (is.undefined(definition)) {
        return undefined;
    }

    const child = commentsGroupChildOf(definition);

    if (is.undefined(child)) {
        return undefined;
    }

    const groupKey = child.groupKey(payload);

    if (is.undefined(groupKey)) {
        return undefined;
    }

    const group = findCommentsGroups(tiles).find(
        (candidate) => candidate.parentType === child.parentType && candidate.commentTile.groupKey === groupKey,
    );

    return is.undefined(group) ? undefined : group.lastMemberIndex + 1;
};

export const commentsGroupCloseSlotKeys = (tiles: readonly ParsedUrlTile[], slotKey: string): ReadonlySet<string> => {
    const group = findCommentsGroups(tiles).find((candidate) => candidate.parentSlotKey === slotKey);

    return new Set(is.undefined(group) ? [slotKey] : [slotKey, ...group.childSlotKeys]);
};
